import { yupResolver } from '@hookform/resolvers/yup'
import { AccountApi } from 'api/AccountApi'
import { ApiError } from 'api/schema/core/ApiError'
import { Button } from 'components/Button/Button'
import ConfirmDialog from 'components/ConfirmDialog/ConfirmDialog'
import PasswordRulesHint from 'components/PasswordRulesHint/PasswordRulesHint'
import TextFieldDescribed from 'components/TextFieldDescribed/TextFieldDescribed'
import Title from 'components/Title/Title'
import UserAvatar from 'components/UserAvatar/UserAvatar'
import { useUser } from 'hooks/user'
import { useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { PROGRESS_TYPES } from 'utils/constants'
import getTestId from 'utils/getTestId'
import { newPasswordSchema } from 'utils/validationSchema'
import * as yup from 'yup'

import style from './AccountProfile.module.css'

interface IFormInputs {
  current_password: string
  new_password: string
  new_password_again: string
}

const fieldErrorFromBody = (body: unknown, field: string): string | null => {
  if (!body || typeof body !== 'object') return null
  const record = body as Record<string, unknown>
  const details = record.details
  const sources: unknown[] = [record, details]
  for (const source of sources) {
    if (!source || typeof source !== 'object') continue
    const value = (source as Record<string, unknown>)[field]
    if (Array.isArray(value) && value.length && typeof value[0] === 'string') return value[0]
    if (typeof value === 'string') return value
  }
  return null
}

const AVATAR_MAX_BYTES = 2 * 1024 * 1024
const AVATAR_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp'])

const AccountProfile = () => {
  const { t } = useTranslation()
  const user = useUser()
  const [progress, setProgress] = useState(PROGRESS_TYPES.IDLE)
  const [formError, setFormError] = useState<string | null>(null)
  const [avatarError, setAvatarError] = useState<string | null>(null)
  const [avatarProgress, setAvatarProgress] = useState(PROGRESS_TYPES.IDLE)
  const [confirmAvatarRemove, setConfirmAvatarRemove] = useState(false)
  const avatarInputRef = useRef<HTMLInputElement>(null)

  const methods = useForm<IFormInputs>({
    defaultValues: {
      current_password: '',
      new_password: '',
      new_password_again: '',
    },
    resolver: yupResolver(
      yup.object().shape({
        current_password: yup.string().required('signin.passwordRequiredError'),
        new_password: newPasswordSchema(),
        new_password_again: yup
          .string()
          .required('signin.passwordRequiredError')
          .oneOf([yup.ref('new_password')], 'account.passwords_mismatch'),
      })
    ),
  })

  const displayName = useMemo(
    () => [user?.state.firstName, user?.state.lastName].filter(Boolean).join(' ') || '—',
    [user?.state.firstName, user?.state.lastName]
  )

  useEffect(() => {
    const subscription = methods.watch((_value, info) => {
      if (
        info.name !== 'current_password' &&
        info.name !== 'new_password' &&
        info.name !== 'new_password_again'
      ) {
        return
      }
      setFormError(null)
      setProgress(prev => (prev === PROGRESS_TYPES.SUCCESS ? PROGRESS_TYPES.IDLE : prev))
      if (info.name === 'new_password' && methods.getValues('new_password_again')) {
        void methods.trigger('new_password_again')
      }
    })
    return () => subscription.unsubscribe()
  }, [methods])

  const onSubmit: SubmitHandler<IFormInputs> = useCallback(
    async data => {
      setFormError(null)
      setProgress(PROGRESS_TYPES.WORK)
      try {
        await AccountApi.changePassword(
          data.current_password,
          data.new_password,
          data.new_password_again
        )
        methods.reset()
        setProgress(PROGRESS_TYPES.SUCCESS)
      } catch (error) {
        const body = error instanceof ApiError ? error.body : undefined
        const currentErr = fieldErrorFromBody(body, 'current_password')
        if (currentErr) {
          methods.setError('current_password', { type: 'server', message: 'account.current_password_incorrect' })
          setProgress(PROGRESS_TYPES.ERROR)
          return
        }
        const fromApi =
          fieldErrorFromBody(body, 'new_password') ||
          fieldErrorFromBody(body, 'new_password_again') ||
          fieldErrorFromBody(body, 'detail')
        setFormError(fromApi || t('account.password_change_error'))
        setProgress(PROGRESS_TYPES.ERROR)
      }
    },
    [methods, t]
  )

  const onAvatarFile = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file) return
    setAvatarError(null)
    if (file.size > AVATAR_MAX_BYTES) {
      setAvatarError(t('account.avatar_too_large'))
      return
    }
    if (file.type && !AVATAR_TYPES.has(file.type)) {
      setAvatarError(t('account.avatar_invalid_type'))
      return
    }
    setAvatarProgress(PROGRESS_TYPES.WORK)
    try {
      await AccountApi.uploadAvatar(file)
      await user?.actions.fetchUser()
      setAvatarProgress(PROGRESS_TYPES.SUCCESS)
    } catch {
      setAvatarError(t('account.avatar_error'))
      setAvatarProgress(PROGRESS_TYPES.ERROR)
    }
  }

  const onAvatarRemove = async () => {
    setAvatarError(null)
    setConfirmAvatarRemove(false)
    setAvatarProgress(PROGRESS_TYPES.WORK)
    try {
      await AccountApi.deleteAvatar()
      await user?.actions.fetchUser()
      setAvatarProgress(PROGRESS_TYPES.SUCCESS)
    } catch {
      setAvatarError(t('account.avatar_error'))
      setAvatarProgress(PROGRESS_TYPES.ERROR)
    }
  }

  return (
    <div className={style.panel} data-test-id={getTestId('account-profile')}>
      <div className={style.headerArea}>
        <Title text={t('account.profile_title')} />
        <div className={style.subtitle}>{t('account.profile_subtitle')}</div>
      </div>

      <div className={style.avatarBlock} data-test-id={getTestId('account-profile-avatar')}>
        <UserAvatar
          url={user?.state.avatarUrl}
          firstName={user?.state.firstName}
          lastName={user?.state.lastName}
          email={user?.state.email}
          size={72}
        />
        <div className={style.avatarActions}>
          <input
            ref={avatarInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            hidden
            onChange={onAvatarFile}
            data-test-id={getTestId('account-profile-avatar-input')}
          />
          <div className={style.avatarButtons}>
            <Button.Outlined
              onClick={() => avatarInputRef.current?.click()}
              loading={avatarProgress === PROGRESS_TYPES.WORK}
              disabled={avatarProgress === PROGRESS_TYPES.WORK}
              dataTestId="account-profile-avatar-upload"
            >
              {t('account.avatar_upload')}
            </Button.Outlined>
            {user?.state.avatarUrl ? (
              <Button.Outlined
                onClick={() => setConfirmAvatarRemove(true)}
                disabled={avatarProgress === PROGRESS_TYPES.WORK}
                dataTestId="account-profile-avatar-remove"
              >
                {t('account.avatar_remove')}
              </Button.Outlined>
            ) : null}
          </div>
          <div className={style.avatarHint}>{t('account.avatar_hint')}</div>
          {avatarError ? (
            <div className={style.errorText} data-test-id={getTestId('account-profile-avatar-error')}>
              {avatarError}
            </div>
          ) : null}
        </div>
      </div>

      <dl className={style.meta}>
        <div className={style.metaRow}>
          <dt>{t('account.email_label')}</dt>
          <dd data-test-id={getTestId('account-profile-email')}>{user?.state.email || '—'}</dd>
        </div>
        <div className={style.metaRow}>
          <dt>{t('account.name_label')}</dt>
          <dd data-test-id={getTestId('account-profile-name')}>{displayName}</dd>
        </div>
      </dl>

      <form className={style.form} onSubmit={methods.handleSubmit(onSubmit)} autoComplete="off">
        <div className={style.formTitle}>{t('account.change_password')}</div>
        <TextFieldDescribed
          id="current_password"
          name="current_password"
          type="password"
          description={t('account.current_password')}
          placeholder={t('account.current_password')}
          autoComplete="current-password"
          value={methods.watch('current_password') || ''}
          onChange={event => methods.setValue('current_password', event.target.value, { shouldDirty: true })}
          hasError={!!methods.formState.errors.current_password}
          error={t(methods.formState.errors.current_password?.message || '')}
          dataTestId={getTestId('account-profile-current-password')}
          labelClassName={style.formLabel}
        />
        <TextFieldDescribed
          id="new_password"
          name="new_password"
          type="password"
          description={t('account.new_password')}
          placeholder={t('account.new_password')}
          autoComplete="new-password"
          value={methods.watch('new_password') || ''}
          onChange={event => methods.setValue('new_password', event.target.value, { shouldDirty: true })}
          hasError={!!methods.formState.errors.new_password}
          error={t(methods.formState.errors.new_password?.message || '')}
          dataTestId={getTestId('account-profile-new-password')}
          labelClassName={style.formLabel}
        />
        <TextFieldDescribed
          id="new_password_again"
          name="new_password_again"
          type="password"
          description={t('account.new_password_again')}
          placeholder={t('account.new_password_again')}
          autoComplete="new-password"
          value={methods.watch('new_password_again') || ''}
          onChange={event => methods.setValue('new_password_again', event.target.value, { shouldDirty: true })}
          hasError={!!methods.formState.errors.new_password_again}
          error={t(methods.formState.errors.new_password_again?.message || '')}
          dataTestId={getTestId('account-profile-new-password-again')}
          labelClassName={style.formLabel}
        />
        <PasswordRulesHint />
        {formError ? (
          <div className={style.errorText} data-test-id={getTestId('account-profile-error')}>
            {formError}
          </div>
        ) : null}
        {progress === PROGRESS_TYPES.SUCCESS ? (
          <div className={style.successText} data-test-id={getTestId('account-profile-success')}>
            {t('account.password_changed')}
          </div>
        ) : null}
        <Button.Standart
          type="submit"
          dataTestId={'account-profile-save'}
          disabled={progress === PROGRESS_TYPES.WORK}
          loading={progress === PROGRESS_TYPES.WORK}
        >
          {t('account.save_password')}
        </Button.Standart>
      </form>
      <ConfirmDialog
        isOpen={confirmAvatarRemove}
        onClose={() => setConfirmAvatarRemove(false)}
        onConfirmClick={onAvatarRemove}
        title={t('account.avatar_remove_confirm_title')}
        confirmText={t('account.avatar_remove_confirm_action')}
        danger
      >
        {t('account.avatar_remove_confirm_text')}
      </ConfirmDialog>
    </div>
  )
}

export default AccountProfile
